import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Search,
  X,
  Inbox,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ArrowUpDown,
  FileText,
} from "lucide-react";
import type { Row } from "../api/client";

export function Button({
  children,
  variant = "",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: string }) {
  return (
    <button
      type="button"
      className={`button ${variant} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
export function IconButton({
  label,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      className="icon-button"
      title={label}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  );
}
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}
export function SearchBox({
  value,
  onChange,
  placeholder = "搜索",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="search">
      <Search size={16} />
      <input
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button aria-label="清空搜索" onClick={() => onChange("")}>
          <X size={13} />
        </button>
      )}
    </label>
  );
}
export function Tabs({
  value,
  onChange,
  tabs,
}: {
  value: string;
  onChange: (id: string) => void;
  tabs: { id: string; label: string; count?: number }[];
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          role="tab"
          aria-selected={value === t.id}
          key={t.id}
          className={value === t.id ? "active" : ""}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {t.count !== undefined && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
export function ErrorNotice({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  if (!error) return null;
  return (
    <div className="error-notice" role="alert">
      <AlertCircle size={16} />
      <span>{error instanceof Error ? error.message : String(error)}</span>
      {retry && <Button onClick={retry}>重新加载</Button>}
    </div>
  );
}
export function Empty({
  title = "暂无内容",
  children,
}: {
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-art">
        <Inbox size={28} />
      </span>
      <span>{title}</span>
      {children}
    </div>
  );
}
export function Loading() {
  return (
    <div className="loading" role="status">
      <Loader2 className="spin" size={18} />
      正在加载
    </div>
  );
}
export function QueryState({
  query,
  children,
}: {
  query: { isPending: boolean; error: unknown; refetch: () => unknown };
  children: ReactNode;
}) {
  return query.isPending ? (
    <Loading />
  ) : query.error ? (
    <ErrorNotice error={query.error} retry={() => query.refetch()} />
  ) : (
    <>{children}</>
  );
}
const labels: Record<string, string> = {
  ok: "通过",
  judged: "未全部通过",
  wrong_answer: "答案错误",
  compile_error: "编译错误",
  runtime_error: "运行错误",
  time_limit: "时间超限",
  timeout: "时间超限",
  memory_limit: "内存超限",
  output_limit: "输出超限",
  partial_correct: "部分正确",
  draft: "草稿",
  published: "已发布",
  archived: "已归档",
  active: "正常",
  disabled: "停用",
  planned: "未开始",
  live: "进行中",
  done: "已结束",
  submitted: "待批改",
  returned: "退回修改",
  graded: "已批改",
  missing: "未提交",
  unmarked: "未点名",
  present: "到课",
  absent: "缺勤",
  leave: "请假",
  late: "迟到",
  easy: "基础",
  medium: "进阶",
  hard: "挑战",
  accepted: "通过",
  completed: "已完成",
  closed: "已关闭",
  overdue: "逾期",
  practice: "练习",
  exam: "考试",
  normal: "正常",
  attention: "需关注",
  high: "需跟进",
  teacher: "教师",
  admin: "管理员",
  single: "单选",
  multiple: "多选",
  boolean: "判断",
  fill: "填空",
  judge: "判断",
  short: "简答",
  coding: "编程",
  scratch: "Scratch",
  file: "普通作业",
  standard: "普通作业",
};
export function label(value: string) {
  return labels[value] || value || "—";
}
export function Badge({ value }: { value?: string }) {
  return (
    <span className={`badge status-${value || "none"}`}>
      <i />
      {label(value || "")}
    </span>
  );
}
export function ObjectCell({
  title,
  sub,
  icon = <FileText size={17} />,
  tone = "blue",
}: {
  title: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: string;
}) {
  return (
    <span className="object-cell">
      <span className={`object-icon ${tone}`}>{icon}</span>
      <span>
        <strong>{title}</strong>
        {sub && <small>{sub}</small>}
      </span>
    </span>
  );
}
export type Column<T> = {
  key: string;
  title: string;
  render?: (row: T) => ReactNode;
  width?: string;
  sort?: (row: T) => string | number;
};
export function Table<T extends Row = Row>({
  rows,
  columns,
  onOpen,
  empty = "暂无记录",
  pageSize = 12,
  pagination = true,
  rowKey,
}: {
  rows: T[];
  columns: Column<T>[];
  onOpen?: (row: T) => void;
  empty?: string;
  pageSize?: number;
  pagination?: boolean;
  rowKey?: (row: T) => string;
}) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: string; direction: number } | null>(
    null,
  );
  useEffect(() => setPage(1), [rows.length]);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pages);
  const col = columns.find((c) => c.key === sort?.key);
  const ordered = col?.sort
    ? [...rows].sort(
        (a, b) =>
          String(col.sort!(a)).localeCompare(String(col.sort!(b)), "zh-CN", {
            numeric: true,
          }) * (sort?.direction || 1),
      )
    : rows;
  if (!rows.length) return <Empty title={empty} />;
  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width }}>
                  {c.sort ? (
                    <button
                      className="sort-button"
                      onClick={() =>
                        setSort({
                          key: c.key,
                          direction: sort?.key === c.key ? -sort.direction : 1,
                        })
                      }
                    >
                      {c.title}
                      <ArrowUpDown size={12} />
                    </button>
                  ) : (
                    c.title
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(pagination ? ordered.slice((safePage - 1) * pageSize, safePage * pageSize) : ordered)
              .map((row) => (
                <tr key={rowKey ? rowKey(row) : row.id}>
                  {columns.map((c, i) => (
                    <td key={c.key}>
                      {onOpen && i === 0 ? (
                        <button
                          className="row-open"
                          onClick={() => onOpen(row)}
                        >
                          {c.render
                            ? c.render(row)
                            : String((row as any)[c.key] ?? "—")}
                        </button>
                      ) : c.render ? (
                        c.render(row)
                      ) : (
                        String((row as any)[c.key] ?? "—")
                      )}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {pagination && (
        <div className="pagination">
          <span>{rows.length} 条记录</span>
          <div>
            <IconButton
              label="上一页"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
            >
              <ChevronLeft size={16} />
            </IconButton>
            <span>
              {safePage} / {pages}
            </span>
            <IconButton
              label="下一页"
              disabled={safePage >= pages}
              onClick={() => setPage(safePage + 1)}
            >
              <ChevronRight size={16} />
            </IconButton>
          </div>
        </div>
      )}
    </>
  );
}
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
  dirty = false,
  busy = false,
  className = "",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  dirty?: boolean;
  busy?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const close = () => {
    if (!busy && (!dirty || window.confirm("有未保存的修改，放弃并关闭？")))
      onClose();
  };
  const latestClose = useRef(close);
  latestClose.current = close;
  useEffect(() => {
    const prior = document.activeElement as HTMLElement | null;
    const dialog = ref.current!;
    dialog.showModal();
    const cancel = (e: Event) => {
      e.preventDefault();
      latestClose.current();
    };
    dialog.addEventListener("cancel", cancel);
    return () => {
      dialog.removeEventListener("cancel", cancel);
      dialog.close();
      prior?.focus();
    };
  }, []);
  useEffect(() => {
    if (!dirty && !busy) return;
    const protect = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, [dirty, busy]);
  return createPortal(
    <dialog
      ref={ref}
      aria-labelledby={id}
      className={`dialog ${wide ? "wide" : ""} ${className}`}
    >
      <header>
        <h2 id={id}>{title}</h2>
        <IconButton label="关闭" disabled={busy} onClick={close}>
          <X size={18} />
        </IconButton>
      </header>
      <div className="dialog-body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </dialog>,
    document.body,
  );
}
export type Field = {
  key: string;
  label: string;
  type?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  placeholder?: string;
  full?: boolean;
};
export function Fields({
  fields,
  value,
  onChange,
}: {
  fields: Field[];
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
}) {
  return (
    <div className="fields">
      {fields.map((f) => (
        <label
          key={f.key}
          className={f.full || f.type === "textarea" ? "full" : ""}
        >
          <span>
            {f.label}
            {f.required && <b className="required">*</b>}
          </span>
          {f.type === "select" ? (
            <select
              aria-label={f.label}
              required={f.required}
              value={value[f.key] ?? ""}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            >
              <option value="">请选择</option>
              {f.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : f.type === "textarea" ? (
            <textarea
              aria-label={f.label}
              rows={5}
              required={f.required}
              placeholder={f.placeholder}
              value={value[f.key] ?? ""}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            />
          ) : (
            <input
              aria-label={f.label}
              type={f.type || "text"}
              min={f.min}
              max={f.max}
              required={f.required}
              placeholder={f.placeholder}
              value={value[f.key] ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  [f.key]:
                    f.type === "number"
                      ? e.target.value === ""
                        ? ""
                        : Number(e.target.value)
                      : e.target.value,
                })
              }
            />
          )}
        </label>
      ))}
    </div>
  );
}
export function EditDialog({
  title,
  initial,
  fields,
  onSave,
  onClose,
  children,
  externalError,
}: {
  externalError?: unknown;
  title: string;
  initial?: Record<string, any>;
  fields: Field[];
  onSave: (value: Record<string, any>) => Promise<boolean | void>;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [value, setValue] = useState(initial || {});
  const baseline = useRef(JSON.stringify(initial || {}));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const id = useId();
  const lock = useRef(false);
  const close = () => {
    if (
      !busy &&
      (JSON.stringify(value) === baseline.current ||
        window.confirm("有未保存的修改，放弃并关闭？"))
    )
      onClose();
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      dirty={JSON.stringify(value) !== baseline.current}
      busy={busy}
      footer={
        <>
          <Button disabled={busy} onClick={close}>
            取消
          </Button>
          <button form={id} className="button primary" disabled={busy}>
            {busy ? "保存中…" : "保存"}
          </button>
        </>
      }
    >
      <form
        id={id}
        onSubmit={async (e) => {
          e.preventDefault();
          if (lock.current) return;
          lock.current = true;
          setBusy(true);
          setError("");
          try {
            if ((await onSave(value)) !== false) onClose();
            else setError("保存未完成，请检查错误信息后重试");
          } catch (e) {
            setError(e instanceof Error ? e.message : "保存失败");
          } finally {
            lock.current = false;
            setBusy(false);
          }
        }}
      >
        <ErrorNotice error={externalError || error} />
        <Fields fields={fields} value={value} onChange={setValue} />
        {children}
      </form>
    </Dialog>
  );
}
export const matches = (
  row: Row,
  term: string,
  keys = ["title", "name", "subject", "username"],
) =>
  keys.some((k) =>
    String(row[k] || "")
      .toLowerCase()
      .includes(term.trim().toLowerCase()),
  );
export const date = (value?: string) => {
  if (!value || value.startsWith("0001-01-01")) return "—";
  if (!/[zZ]|[+-]\d\d:\d\d$/.test(value))
    return value.replace("T", " ").slice(0, 16);
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp)
    ? new Date(timestamp + 8 * 3600000)
        .toISOString()
        .slice(0, 16)
        .replace("T", " ")
    : value;
};
export function csv(rows: Row[], columns: [string, string][], name: string) {
  const quote = (v: any) =>
    `"${String(v ?? "")
      .replace(/^[=+@-]/, "'$&")
      .replaceAll('"', '""')}"`;
  const body = [
    columns.map((c) => c[1]),
    ...rows.map((r) => columns.map((c) => r[c[0]])),
  ]
    .map((r) => r.map(quote).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(
    new Blob(["\uFEFF", body], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
